import React, { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Box,
  Typography,
  Popper,
  ClickAwayListener,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { VariableHierarchyNode, VariableSeleccionada } from '../../types';

interface VariableComboSelectorProps {
  hierarchy: VariableHierarchyNode[];
  selectedVariables: VariableSeleccionada[];
  onVariableSelect: (variableId: string, sensorId: string, sensorNombre: string | null) => void;
  onVariableRemove?: (variableId: string, sensorId: string) => void;
  placeholder?: string;
  color?: string;
  ejeVariables?: VariableSeleccionada[];
  colorMap?: Record<string, string>;
}

export const VariableComboSelector: React.FC<VariableComboSelectorProps> = ({
  hierarchy,
  selectedVariables,
  onVariableSelect,
  onVariableRemove,
  placeholder,
  color,
  ejeVariables = [],
  colorMap,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const defaultPlaceholder = placeholder || 'Seleccionar variable...';
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const anchorRef = useRef<HTMLDivElement>(null);

  const selectedIds = useMemo(
    () =>
      new Set(
        selectedVariables.map((v) => `${v.variable_id}-${v.sensor_id}`)
      ),
    [selectedVariables]
  );

  const countAllVariables = (nodes: VariableHierarchyNode[]): number => {
    let count = 0;
    for (const node of nodes) {
      if (node.tipo === 'variable') {
        count++;
      }
      if (node.children && node.children.length > 0) {
        count += countAllVariables(node.children);
      }
    }
    return count;
  };

  const totalVariablesAvailable = useMemo(() => countAllVariables(hierarchy), [hierarchy]);

  const filteredHierarchy = useMemo(() => {
    if (!searchText.trim()) return hierarchy;

    const filterLower = searchText.toLowerCase();
    
    const filterNode = (node: VariableHierarchyNode): VariableHierarchyNode | null => {
      const matchesFilter = 
        node.nombre.toLowerCase().includes(filterLower) ||
        (node.tipo === 'variable' && node.variable_unidad?.toLowerCase().includes(filterLower));

      if (matchesFilter) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        const filteredChildren = node.children
          .map(child => filterNode(child))
          .filter((child): child is VariableHierarchyNode => child !== null);

        if (filteredChildren.length > 0) {
          return {
            ...node,
            children: filteredChildren,
          };
        }
      }

      return null;
    };

    return hierarchy
      .map(node => filterNode(node))
      .filter((node): node is VariableHierarchyNode => node !== null);
  }, [hierarchy, searchText]);

  const handleVariableClick = (
    variableId: string,
    sensorId: string,
    sensorNombre: string | null
  ) => {
    const id = `${variableId}-${sensorId}`;
    if (!selectedIds.has(id)) {
      onVariableSelect(variableId, sensorId, sensorNombre);
      setOpen(false);
      setSearchText('');
    }
  };

  const renderTreeItem = (node: VariableHierarchyNode, level: number = 0): React.ReactNode => {
    const nodeId = node.id;
    const hasChildren = node.children && node.children.length > 0;

    if (node.tipo === 'variable') {
      const id = `${node.variable_id}-${node.sensor_id}`;
      if (selectedIds.has(id)) {
        return null;
      }

      let sensorNombre: string | null = null;
      for (const tipologiaNode of filteredHierarchy) {
        if (tipologiaNode.children) {
          const sensorNode = tipologiaNode.children.find(
            (n) => n.sensor_id === node.sensor_id && n.tipo === 'sensor'
          );
          if (sensorNode) {
            sensorNombre = sensorNode.nombre;
            break;
          }
        }
      }

      return (
        <TreeItem
          key={nodeId}
          itemId={nodeId}
          label={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
              onClick={() => {
                if (node.variable_id && node.sensor_id) {
                  handleVariableClick(node.variable_id, node.sensor_id, sensorNombre);
                }
              }}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                {node.nombre}
              </Typography>
              {node.variable_unidad && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    ml: 1,
                  }}
                >
                  ({node.variable_unidad})
                </Typography>
              )}
            </Box>
          }
          sx={{
            '& .MuiTreeItem-content': {
              paddingLeft: `${level * 24 + 8}px`,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              },
            },
          }}
        />
      );
    }

    return (
      <TreeItem
        key={nodeId}
        itemId={nodeId}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
            <Typography
              variant={node.tipo === 'tipologia' ? 'subtitle2' : 'body2'}
              sx={{
                fontWeight: node.tipo === 'tipologia' ? 600 : 500,
                color: theme.palette.text.primary,
              }}
            >
              {node.nombre}
            </Typography>
          </Box>
        }
        sx={{
          '& .MuiTreeItem-content': {
            paddingLeft: `${level * 24 + 8}px`,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          },
        }}
      >
        {hasChildren && node.children?.map(child => renderTreeItem(child, level + 1))}
      </TreeItem>
    );
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  const getSensorNombre = (variable: VariableSeleccionada): string => {
    let nombre = variable.sensor_nombre;
    if (!nombre) {
      for (const tipologiaNode of hierarchy) {
        if (tipologiaNode.children) {
          const sensorNode = tipologiaNode.children.find(
            (node) => node.sensor_id === variable.sensor_id && node.tipo === 'sensor'
          );
          if (sensorNode) {
            nombre = sensorNode.nombre;
            break;
          }
        }
      }
    }
    return nombre || `Sensor ${variable.sensor_id.slice(0, 8)}`;
  };

  return (
    <Box ref={anchorRef} sx={{ width: '100%', position: 'relative' }}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: 40,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          backgroundColor: color ? alpha(color, 0.05) : theme.palette.background.paper,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center',
          padding: '4px 8px',
          cursor: 'text',
          '&:hover': {
            borderColor: color || theme.palette.primary.main,
            backgroundColor: color ? alpha(color, 0.1) : theme.palette.background.paper,
          },
          '&:focus-within': {
            borderColor: color || theme.palette.primary.main,
            borderWidth: 2,
            padding: '3px 7px',
          },
        }}
        onClick={() => {
          const input = anchorRef.current?.querySelector('input');
          if (input) {
            input.focus();
          }
          setOpen(true);
        }}
      >
        {ejeVariables.map((variable) => {
          const variableKey = `${variable.variable_id}-${variable.sensor_id}`;
          const chipColor = variable.color || colorMap?.[variableKey] || color || theme.palette.primary.main;
          const sensorNombre = getSensorNombre(variable);
          const label = `${sensorNombre} - ${variable.variable_nombre} (${variable.variable_unidad})`;
          
          return (
            <Chip
              key={variableKey}
              label={label}
              size="small"
              onDelete={(e) => {
                e.stopPropagation();
                if (onVariableRemove) {
                  onVariableRemove(variable.variable_id, variable.sensor_id);
                }
              }}
              deleteIcon={<CloseIcon />}
              onClick={(e) => e.stopPropagation()}
              sx={{
                backgroundColor: chipColor,
                color: theme.palette.getContrastText(chipColor),
                height: 28,
                fontSize: '0.75rem',
                maxWidth: '250px',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  paddingLeft: 1,
                  paddingRight: 0.5,
                },
                '&:hover': {
                  backgroundColor: alpha(chipColor, 0.85),
                },
                '& .MuiChip-deleteIcon': {
                  color: theme.palette.getContrastText(chipColor),
                  fontSize: '1.1rem',
                  marginRight: 0.5,
                  '&:hover': {
                    color: theme.palette.getContrastText(chipColor),
                    opacity: 0.8,
                  },
                },
                fontWeight: 500,
              }}
            />
          );
        })}
        <Box
          component="input"
          type="text"
          placeholder={ejeVariables.length === 0 ? defaultPlaceholder : ''}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onFocus={() => setOpen(true)}
          sx={{
            border: 'none',
            outline: 'none',
            backgroundColor: 'transparent',
            flex: 1,
            minWidth: '120px',
            fontSize: '0.875rem',
            fontFamily: theme.typography.fontFamily,
            color: theme.palette.text.primary,
            padding: '4px 0',
            '&::placeholder': {
              color: theme.palette.text.secondary,
              opacity: 0.6,
            },
          }}
        />
      </Box>
      {open && (
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 4],
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={handleClickAway}>
            <Paper
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                width: '100%',
                mt: 0.5,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[8],
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {(() => {
                const countAvailableVariables = (nodes: VariableHierarchyNode[]): number => {
                  let count = 0;
                  for (const node of nodes) {
                    if (node.tipo === 'variable') {
                      const id = `${node.variable_id}-${node.sensor_id}`;
                      if (!selectedIds.has(id)) {
                        count++;
                      }
                    }
                    if (node.children && node.children.length > 0) {
                      count += countAvailableVariables(node.children);
                    }
                  }
                  return count;
                };

                const availableCount = countAvailableVariables(filteredHierarchy);
                const allSelected = totalVariablesAvailable > 0 && selectedIds.size >= totalVariablesAvailable;
                const noVariablesAtAll = totalVariablesAvailable === 0;

                if (noVariablesAtAll) {
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 100,
                        p: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No hay variables disponibles
                      </Typography>
                    </Box>
                  );
                }

                if (allSelected || availableCount === 0) {
                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 100,
                        p: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {searchText.trim() 
                          ? 'No se encontraron resultados'
                          : 'Todas las variables están seleccionadas'}
                      </Typography>
                    </Box>
                  );
                }

                return (
                <Box sx={{ p: 1 }}>
                  <SimpleTreeView
                    slots={{
                      collapseIcon: ExpandMoreIcon,
                      expandIcon: ChevronRightIcon,
                    }}
                    sx={{
                      '& .MuiTreeItem-content': {
                        borderRadius: 0.5,
                        mb: 0.25,
                      },
                    }}
                  >
                    {filteredHierarchy.map((node) => renderTreeItem(node))}
                  </SimpleTreeView>
                </Box>
                );
              })()}
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </Box>
  );
};







